import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { Loader2, HardHat, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';

const fmtGbp = (pounds) => `£${(Number(pounds) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Labour log — plain list of every person-day logged in the diary for the
 * project, grouped by day, costed at the person's day rate. Overview only:
 * tap a day to open it in the diary.
 */
export function LabourLog({ projectId, diaryPrefix, onData }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/diary/projects/${projectId}/labour-log`)
      .then(r => { setData(r.data); onData?.(r.data); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [api, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: PAPER.accent }} /></div>;

  const rows = data?.rows || [];
  const ledgerRows = data?.ledger_rows || [];
  if (rows.length === 0 && ledgerRows.length === 0) {
    return (
      <div className="rounded-xl px-4 py-10 text-center" style={{ background: PAPER.card, border: `1px dashed ${PAPER.border}` }} data-testid="labour-log-empty">
        <HardHat className="w-7 h-7 mx-auto mb-2" style={{ color: PAPER.hint }} />
        <div className="text-[14px] font-semibold" style={{ color: PAPER.ink }}>No labour logged yet</div>
        <div className="text-[12px] mt-1" style={{ color: PAPER.muted }}>Log who was on site from the diary and each day will appear here.</div>
      </div>
    );
  }

  const days = [];
  for (const r of rows) {
    const last = days[days.length - 1];
    if (last && last.date === r.date) { last.rows.push(r); last.cost += r.cost_pence; }
    else days.push({ date: r.date, rows: [r], cost: r.cost_pence });
  }

  return (
    <div className="space-y-3" data-testid="labour-log-list">
      {ledgerRows.length > 0 && (
        <div className="rounded-xl px-3.5 py-3" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }} data-testid="labour-log-ledger">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>Invoiced labour · Cost Inbox, manual & opening balance</div>
            <div className="text-[12px] font-bold tabular-nums" style={{ color: PAPER.ink }} data-testid="labour-log-ledger-total">{fmtGbp(data.ledger_total)}</div>
          </div>
          <div className="space-y-1.5">
            {ledgerRows.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 text-[13px]" data-testid={`labour-log-ledger-row-${r.id}`}>
                <div className="min-w-0 truncate" style={{ color: PAPER.ink }}>
                  <span className="font-semibold">{r.source === 'opening_balance' ? 'Opening balance' : (r.supplier_name || r.reference || 'Manual entry')}</span>
                  {r.supplier_name && r.reference && <span className="ml-1.5 text-[11px]" style={{ color: PAPER.muted }}>{r.reference}</span>}
                  {r.date && <span className="ml-1.5 text-[11px]" style={{ color: PAPER.muted }}>{format(parseISO(r.date), 'dd MMM yyyy')}</span>}
                  {r.reason && <span className="ml-1.5 text-[11px]" style={{ color: PAPER.muted }}>· {r.reason}</span>}
                </div>
                <div className="flex-shrink-0 text-[12px] tabular-nums font-bold" style={{ color: PAPER.ink }}>{fmtGbp(r.value)}</div>
              </div>
            ))}
          </div>
          {rows.length > 0 && (
            <div className="mt-2 pt-2 text-[11px]" style={{ color: PAPER.muted, borderTop: `1px solid ${PAPER.divider}` }}>
              Employee diary days {fmtGbp(data.diary_employee_total ?? data.diary_total)} + invoiced {fmtGbp(data.ledger_total)} = <span className="font-semibold" style={{ color: PAPER.ink }}>{fmtGbp(data.total)}</span> counted
              {(data.awaiting_invoice_total || 0) > 0 && <span> · sub-contractor diary days {fmtGbp(data.awaiting_invoice_total)} not yet invoiced (in the running total, not counted)</span>}
            </div>
          )}
        </div>
      )}

      {(data?.by_person?.length > 0) && (
        <div className="rounded-xl px-3.5 py-3" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }} data-testid="labour-log-by-person">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: PAPER.muted }}>
            <span>By person</span><span className="text-right">Diary</span><span className="text-right w-[76px]">Invoiced</span>
          </div>
          <div className="space-y-1.5">
            {data.by_person.map(p => (
              <div key={p.person_id || p.name} className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-center text-[13px]" data-testid={`labour-log-person-${p.person_id || 'manual'}`}>
                <div className="min-w-0 truncate" style={{ color: PAPER.ink }}>
                  <span className="font-semibold">{p.name}</span>
                  {p.trade && <span className="ml-1.5 text-[11px]" style={{ color: PAPER.muted }}>{p.trade}</span>}
                  {p.counted === false && (p.invoiced_pence || 0) === 0 && p.days > 0 && <span className="ml-1.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#B45309' }} data-testid={`labour-log-person-awaiting-${p.person_id || 'manual'}`}>not yet invoiced</span>}
                </div>
                <div className="flex-shrink-0 text-[12px] tabular-nums text-right" style={{ color: PAPER.inkSoft }}>
                  {p.days > 0 ? <>{p.days} {p.days === 1 ? 'day' : 'days'} · <span className="font-bold" style={{ color: p.counted === false ? '#047857' : PAPER.ink }}>{fmtGbp(p.cost)}</span></> : <span style={{ color: PAPER.hint }}>—</span>}
                </div>
                <div className="flex-shrink-0 w-[76px] text-right text-[12px] tabular-nums font-bold" style={{ color: (p.invoiced_pence || 0) > 0 ? '#0369A1' : PAPER.hint }} data-testid={`labour-log-person-invoiced-${p.person_id || 'manual'}`}>
                  {(p.invoiced_pence || 0) > 0 ? fmtGbp(p.invoiced) : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {days.map(d => (
        <div key={d.date} className="rounded-xl overflow-hidden" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }} data-testid={`labour-log-day-${d.date}`}>
          <button
            type="button"
            onClick={() => navigate(`${diaryPrefix}/${projectId}`, { state: { seedDate: d.date } })}
            className="w-full flex items-center justify-between px-3.5 py-2 text-left hover:bg-[#FAF7F2]"
            style={{ borderBottom: `1px solid ${PAPER.divider}` }}
            data-testid={`labour-log-day-open-${d.date}`}
            title="Open this day in the diary"
          >
            <span className="text-[12px] font-semibold" style={{ color: PAPER.inkSoft }}>{format(parseISO(d.date), 'EEE dd MMM yyyy')}</span>
            <span className="text-[13px] font-bold tabular-nums" style={{ color: PAPER.ink }}>{fmtGbp(d.cost / 100)}</span>
          </button>
          <div className="px-3.5 py-1.5">
            {d.rows.map((r, i) => (
              <div key={`${r.entry_id}-${r.person_id || r.name}-${i}`} className="flex items-center justify-between gap-3 py-1.5 text-[13px]" data-testid={`labour-log-row-${r.entry_id}-${i}`}>
                <div className="min-w-0 truncate" style={{ color: PAPER.ink }}>
                  {r.name}
                  {r.trade && <span className="ml-1.5 text-[11px]" style={{ color: PAPER.muted }}>{r.trade}</span>}
                </div>
                {r.missing_rate ? (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#B45309' }}>
                    <AlertTriangle className="w-3 h-3" /> no rate
                  </span>
                ) : (
                  <span className="flex-shrink-0 text-[13px] tabular-nums" style={{ color: PAPER.inkSoft }}>
                    {r.unit === 'hours'
                      ? <>{r.hours}h · <span className="font-semibold" style={{ color: PAPER.ink }}>{fmtGbp(r.cost)}</span></>
                      : fmtGbp(r.rate)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
